import React, { Component } from 'react';
import ReactEcharts from 'echarts-for-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { descargarChartPng } from '../../commons/utils/chartPng.js';

// Mapa locale → código de moneda (mismo que EricAnalyst).
var LOCALE_TO_CURRENCY = {
  'es-AR': 'ARS', 'pt-BR': 'BRL', 'en-US': 'USD',
  'es-CL': 'CLP', 'es-CO': 'COP', 'es-MX': 'MXN',
  'es-UY': 'UYU', 'es-PY': 'PYG',
};

function getEricCurrency() {
  var locale = ConfigBusiness.get('generales.formatoRegional') || 'es-AR';
  return LOCALE_TO_CURRENCY[locale] || 'ARS';
}

/**
 * Formatea un valor de eje/serie según el campo `formato` de la chart_spec.
 * formato ∈ 'numero' | 'moneda' | 'porcentaje'
 */
function formatearValor(v, formato) {
  if (v === null || v === undefined) return '';
  if (formato === 'moneda') {
    return LocaleFormat.formatCurrency(v, getEricCurrency());
  }
  if (formato === 'porcentaje') {
    return LocaleFormat.formatNumber(v, 2) + '%';
  }
  // 'numero' u omitido
  var n = Number(v);
  if (!isNaN(n)) {
    return LocaleFormat.formatNumber(n, Number.isInteger(n) ? 0 : 2);
  }
  return String(v);
}

/**
 * Traduce una chart_spec genérica + filas de datos → opción de ECharts 4.x.
 *
 * Soporta: bar, line, area, pie, doughnut, scatter.
 * El mapa de calor (mapa_calor) se maneja aparte con Leaflet.
 *
 * @param {object} chartSpec  - chart_spec del micro
 * @param {Array}  filas      - filas del dataset
 * @returns {object}  opción para ECharts, o null si no se puede traducir
 */
// Fallback de ancho para el primer render, antes de poder medir el contenedor
// (y para los tests). En cuanto el componente monta, se usa el ancho REAL: el
// chart es width:100% dentro de una burbuja con max-width:80%, así que el ancho
// depende del layout y no puede fijarse por constante.
var ANCHO_PLOT_FALLBACK = 700;
// Banda que se comen las etiquetas y el nombre del eje Y a la izquierda.
var GUTTER_EJE_Y = 70;
// Ancho medio de un caracter a 12px. Los nombres de servicio llegan en mayúsculas,
// que son más anchas que la minúscula promedio.
var PX_POR_CHAR = 7;
// Aire mínimo entre etiquetas vecinas. Sin esto la cuenta da "entran" cuando en
// realidad quedan pegadas: con 6 servicios el caso quedaba 126px vs 126px
// disponibles y no rotaba, que es exactamente el gráfico que se veía pisado.
var PADDING_LABEL = 10;
// Corte de las etiquetas largas. El nombre completo sigue estando en el tooltip,
// así que truncar no pierde información.
var MAX_CHARS_LABEL = 18;
var MIN_CHARS_LABEL = 8;
// Divisor para escalar el corte al ancho disponible. Una etiqueta rotada se centra
// sobre su tick y sobresale hacia los costados, así que en paneles angostos la
// primera y la última se salían del gráfico. Calibrado midiendo el render: 40 es el
// valor más permisivo que no recorta ninguna etiqueta entre 400px y 1100px.
var DIVISOR_CHARS = 40;
var ALTO_LINEA = 14;
// Piso del margen izquierdo: lo que necesitan las etiquetas del eje Y.
var MARGEN_IZQ_MIN = 55;

/**
 * Decide rotación y truncado de las etiquetas del eje X de categorías.
 *
 * La heurística anterior miraba solo la CANTIDAD de etiquetas (`length > 10`), así
 * que seis nombres largos ("SISTEMA DE LUCES 2025") se pisaban igual que doce
 * cortos. Lo que produce el solapamiento es el ancho que ocupa cada etiqueta
 * contra el ancho disponible por categoría, no cuántas son.
 *
 * Al rotar, el espacio horizontal que necesita una etiqueta deja de depender de su
 * largo y pasa a depender del alto de línea: dos etiquetas rotadas θ no se pisan si
 * están separadas al menos `altoLinea / sin(θ)`. De ahí sale la escalera 30/45/90.
 *
 * @param {Array} xLabels etiquetas del eje
 * @param {number} [anchoContenedor] ancho real del chart en px. Sin esto se cae al
 *        fallback, que solo acierta si el panel mide lo que se supuso.
 * @return {{rotate: number, alto: number, maxChars: number, interval: (number|string)}}
 *         alto = px que ocupan las etiquetas ya rotadas (sirve para bajar el
 *         nombre del eje); interval = 0 muestra todas, 'auto' deja que ECharts
 *         esconda las que no entren.
 */
export function calcularEjeXLabels(xLabels, anchoContenedor) {
  var n = Math.max(xLabels.length, 1);
  var maxLen = 0;
  xLabels.forEach(function(l) {
    maxLen = Math.max(maxLen, String(l).length);
  });

  var anchoPlot = (anchoContenedor > 0)
    ? Math.max(anchoContenedor - GUTTER_EJE_Y, 80)
    : ANCHO_PLOT_FALLBACK;

  var maxChars = Math.max(
    MIN_CHARS_LABEL,
    Math.min(MAX_CHARS_LABEL, Math.floor(anchoPlot / DIVISOR_CHARS))
  );

  var anchoEtiqueta = Math.min(maxLen, maxChars) * PX_POR_CHAR;
  var anchoPorCategoria = anchoPlot / n;

  var rotate = 0;
  if (anchoEtiqueta + PADDING_LABEL > anchoPorCategoria) {
    if (anchoPorCategoria >= ALTO_LINEA / Math.sin(Math.PI / 6)) {
      rotate = 30;
    } else if (anchoPorCategoria >= ALTO_LINEA / Math.sin(Math.PI / 4)) {
      rotate = 45;
    } else {
      rotate = 90;
    }
  }

  // Red de seguridad: si ni siquiera en vertical entra una etiqueta por categoría,
  // forzar todas las etiquetas las dibujaría una encima de otra. En ese caso se
  // delega en ECharts, que esconde las que no entran. Preferimos mostrar de menos
  // antes que mostrar ilegible; el dato completo sigue en el tooltip y en la tabla.
  var separacionUtil = rotate === 0
    ? anchoPorCategoria
    : anchoPorCategoria * Math.sin(rotate * Math.PI / 180);
  var interval = (separacionUtil < ALTO_LINEA) ? 'auto' : 0;

  var alto = rotate === 0
    ? ALTO_LINEA
    : Math.round(anchoEtiqueta * Math.sin(rotate * Math.PI / 180)) + 6;

  // Margen izquierdo. La etiqueta rotada se ancla por su extremo derecho sobre el
  // tick y se extiende hacia abajo-izquierda, así que la primera sobresale del
  // gráfico. `containLabel` no reserva ese espacio (subestima el texto rotado), y
  // hay que pedirlo explícitamente: lo que sobresale es el ancho proyectado de la
  // etiqueta menos la media categoría que ya hay antes del primer tick.
  var overhang = rotate === 0
    ? 0
    : anchoEtiqueta * Math.cos(rotate * Math.PI / 180) - anchoPorCategoria / 2;
  var izquierda = Math.round(Math.max(MARGEN_IZQ_MIN, overhang));

  return {
    rotate: rotate,
    alto: alto,
    maxChars: maxChars,
    interval: interval,
    izquierda: izquierda,
  };
}

function buildEchartsOption(chartSpec, filas, anchoContenedor) {
  if (!chartSpec || !chartSpec.tipo || chartSpec.tipo === 'mapa_calor') return null;
  if (!chartSpec.eje_x || !chartSpec.serie) return null;
  if (!filas || filas.length === 0) return null;

  var tipo     = chartSpec.tipo;
  var campoX   = chartSpec.eje_x.campo;
  var campoY   = chartSpec.serie.campo;
  var formato  = chartSpec.serie.formato || 'numero';
  var labelX   = chartSpec.eje_x.label || campoX;
  var labelY   = chartSpec.serie.label || campoY;
  var titulo   = chartSpec.titulo || '';
  var labels   = chartSpec.labels || [];

  var xData = filas.map(function(f) { return f[campoX] !== undefined ? String(f[campoX]) : ''; });
  var yData = filas.map(function(f) { return f[campoY] !== undefined ? Number(f[campoY]) : 0; });

  // Si el micro incluyó labels formateados, preferirlos sobre los valores raw del campo X.
  var xLabels = (labels.length === xData.length) ? labels : xData;

  // ---- PIE / DOUGHNUT ----
  if (tipo === 'pie' || tipo === 'doughnut') {
    var pieData = xLabels.map(function(label, i) {
      return { name: label, value: yData[i] };
    });
    return {
      title: titulo ? {
        text: titulo,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return params.name + '<br/>' + labelY + ': '
            + formatearValor(params.value, formato)
            + ' (' + params.percent + '%)';
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        type: 'scroll',
      },
      series: [{
        type: 'pie',
        radius: tipo === 'doughnut' ? ['40%', '70%'] : '60%',
        center: ['50%', '45%'],
        data: pieData,
        label: {
          formatter: function(params) {
            return params.name + '\n' + formatearValor(params.value, formato);
          },
        },
      }],
    };
  }

  // ---- SCATTER ----
  if (tipo === 'scatter') {
    var scatterData = filas.map(function(f) {
      return [f[campoX] !== undefined ? Number(f[campoX]) : 0,
              f[campoY] !== undefined ? Number(f[campoY]) : 0];
    });
    return {
      title: titulo ? {
        text: titulo,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return labelX + ': ' + formatearValor(params.value[0], 'numero')
            + '<br/>' + labelY + ': ' + formatearValor(params.value[1], formato);
        },
      },
      xAxis: { name: labelX, type: 'value' },
      yAxis: {
        name: labelY,
        type: 'value',
        axisLabel: {
          formatter: function(v) { return formatearValor(v, formato); },
        },
      },
      series: [{
        type: 'scatter',
        data: scatterData,
        symbolSize: 7,
      }],
    };
  }

  // ---- BAR, LINE, AREA ----
  var echartsType = (tipo === 'line' || tipo === 'area') ? 'line' : 'bar';
  var serieExtra = tipo === 'area' ? { areaStyle: {} } : {};
  var ejeX = calcularEjeXLabels(xLabels, anchoContenedor);

  return {
    title: titulo ? {
      text: titulo,
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
    } : undefined,
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        var p = params[0];
        return p.name + '<br/>' + labelY + ': ' + formatearValor(p.value, formato);
      },
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      name: labelX,
      // El nombre del eje iba al final (default 'end'), donde el borde del
      // contenedor lo recortaba ("Servicio" se veía como "Se"). Centrado y por
      // debajo de las etiquetas ya rotadas.
      nameLocation: 'middle',
      nameGap: ejeX.alto + 16,
      axisLabel: {
        interval: ejeX.interval,
        rotate: ejeX.rotate,
        // Ancla la etiqueta por su esquina superior derecha sobre el tick, de modo
        // que cuelgue entera POR DEBAJO del eje. Centrarla (`align: 'center'`)
        // dejaba la mitad del texto por encima de la línea del eje, invadiendo el
        // área del gráfico y quedando tapada por las barras. El costo de anclar a
        // derecha es que la primera etiqueta sobresale por izquierda; eso se
        // compensa con `grid.left` (ver `izquierda`).
        align: ejeX.rotate === 0 ? undefined : 'right',
        verticalAlign: ejeX.rotate === 0 ? undefined : 'top',
        formatter: function(v) {
          var s = String(v);
          return s.length > ejeX.maxChars ? s.slice(0, ejeX.maxChars - 1) + '…' : s;
        },
      },
    },
    yAxis: {
      type: 'value',
      name: labelY,
      axisLabel: {
        formatter: function(v) { return formatearValor(v, formato); },
      },
    },
    // Con containLabel las etiquetas ya entran solas; `bottom` solo reserva el
    // renglón del nombre del eje, que queda fuera de ese bounding box.
    grid: { containLabel: true, left: ejeX.izquierda, right: '4%', bottom: 26 },
    series: [Object.assign({}, serieExtra, {
      type: echartsType,
      data: yData,
      smooth: tipo === 'line' || tipo === 'area',
    })],
  };
}

/**
 * EricChart — renderiza un gráfico ECharts a partir de una chart_spec genérica.
 *
 * Props:
 *   chartSpec  {object}  - chart_spec del microservicio
 *   filas      {Array}   - dataset (filas del response)
 *   columnas   {Array}   - columnas del dataset
 *   fechaDato  {string}  - ISO string del created_at del mensaje (para aviso de antigüedad)
 *   intl       {object}  - objeto intl de react-intl (para i18n)
 *   mapaId     {string}  - id único para el container del mapa Leaflet (mapa_calor)
 *
 * Uso típico desde EricAnalyst:
 *   <EricChart chartSpec={mensaje.chartSpec} filas={mensaje.filas}
 *              columnas={mensaje.columnas} fechaDato={mensaje.fechaEnvio}
 *              intl={this.props.intl} mapaId={'eric-mapa-' + index} />
 */
export class EricChart extends Component {
  constructor(props) {
    super(props);
    this._mapaRef = React.createRef();
    this._wrapperRef = React.createRef();
    this._mapaLeaflet = null;
    this._echartsComp = null;
    this._resizeTimer = null;
    // El ancho arranca en null y se mide al montar: la rotación de las etiquetas
    // depende del ancho real, que varía con el layout (sidebar, tamaño de ventana,
    // burbuja al 80%). Ver calcularEjeXLabels().
    this.state = { anchoContenedor: null };
    this._onResize = this._onResize.bind(this);
  }

  componentDidMount() {
    if (this.props.chartSpec && this.props.chartSpec.tipo === 'mapa_calor') {
      this._inicializarMapa();
    }
    this._medirAncho();
    window.addEventListener('resize', this._onResize);
  }

  _onResize() {
    // La rotación se recalcula al terminar el resize, no en cada frame.
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    var self = this;
    this._resizeTimer = setTimeout(function() { self._medirAncho(); }, 150);
  }

  _medirAncho() {
    var el = this._wrapperRef.current;
    if (!el) return;
    var ancho = el.offsetWidth;
    if (ancho > 0 && ancho !== this.state.anchoContenedor) {
      this.setState({ anchoContenedor: ancho });
    }
  }

  componentDidUpdate(prevProps) {
    var spec = this.props.chartSpec;
    var prevSpec = prevProps.chartSpec;
    if (!spec || spec.tipo !== 'mapa_calor') return;
    // Si el spec o los datos cambiaron, re-inicializar el mapa.
    if (spec !== prevSpec || this.props.filas !== prevProps.filas) {
      if (this._mapaLeaflet) {
        this._mapaLeaflet.remove();
        this._mapaLeaflet = null;
      }
      this._inicializarMapa();
    }
  }

  componentWillUnmount() {
    if (this._mapaLeaflet) {
      this._mapaLeaflet.remove();
      this._mapaLeaflet = null;
    }
    window.removeEventListener('resize', this._onResize);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
  }

  _inicializarMapa() {
    var spec = this.props.chartSpec;
    if (!spec || spec.tipo !== 'mapa_calor') return;
    var el = this._mapaRef.current;
    if (!el) return;
    if (el._leaflet_id) return; // ya inicializado

    var colLat = spec.eje_x && spec.eje_x.campo;
    var colLon = spec.serie && spec.serie.campo;
    if (!colLat || !colLon) return;

    var filas = this.props.filas || [];
    var puntos = [];
    filas.forEach(function(f) {
      var lat = parseFloat(f[colLat]);
      var lon = parseFloat(f[colLon]);
      if (!isNaN(lat) && !isNaN(lon)) {
        puntos.push([lat, lon]);
      }
    });
    if (puntos.length === 0) return;

    var sumLat = 0;
    var sumLon = 0;
    puntos.forEach(function(p) { sumLat += p[0]; sumLon += p[1]; });
    var centerLat = sumLat / puntos.length;
    var centerLon = sumLon / puntos.length;

    var map = L.map(el);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    L.heatLayer(puntos, { radius: 25, blur: 15, minOpacity: 0.4 }).addTo(map);
    map.setView([centerLat, centerLon], 8);
    this._mapaLeaflet = map;
  }

  _nombreArchivo() {
    var spec = this.props.chartSpec;
    if (spec && spec.titulo) {
      return spec.titulo.replace(/[^a-zA-Z0-9_\-\s]/g, '').trim().replace(/\s+/g, '_') || 'datos_eric';
    }
    return 'datos_eric';
  }

  // Texto "datos al {fecha}" compartido por el aviso visible y el caption del PNG.
  // Devuelve null si no hay fecha del dato.
  _captionFecha() {
    var fechaDato = this.props.fechaDato;
    if (!fechaDato) return null;
    var formateada = LocaleFormat.formatDate(fechaDato);
    if (!formateada) return null;
    var intl = this.props.intl;
    return intl
      ? intl.formatMessage({ id: 'eric.chart.datos_al', defaultMessage: 'datos al {fecha}' }, { fecha: formateada })
      : ('datos al ' + formateada);
  }

  renderAviso() {
    var texto = this._captionFecha();
    if (!texto) return null;
    return (
      <div className="eric-chart-aviso-fecha">
        <i className="fa fa-clock-o mr-1" aria-hidden="true"></i>
        {texto}
      </div>
    );
  }

  // Método público: lo invoca EricAnalyst desde la fila de acciones del mensaje
  // (junto a like/dislike/copiar) vía ref. No-op si el gráfico es un mapa de calor
  // (Leaflet, sin instancia de ECharts).
  descargarPng() {
    var inst = (this._echartsComp && typeof this._echartsComp.getEchartsInstance === 'function')
      ? this._echartsComp.getEchartsInstance()
      : null;
    if (!inst) return;
    var spec = this.props.chartSpec || {};
    descargarChartPng(inst, {
      titulo: spec.titulo || '',
      caption: this._captionFecha() || '',
      nombreArchivo: this._nombreArchivo(),
    });
  }

  render() {
    var spec    = this.props.chartSpec;
    var filas   = this.props.filas || [];

    if (!spec || !spec.tipo) return null;

    // ---- MAPA DE CALOR ----
    if (spec.tipo === 'mapa_calor') {
      return (
        <div className="eric-chart-wrapper">
          {spec.titulo && <div className="eric-chart-titulo">{spec.titulo}</div>}
          <div className="eric-mapa-calor-wrapper">
            <div ref={this._mapaRef} className="eric-mapa-calor-container" />
          </div>
          {this.renderAviso()}
        </div>
      );
    }

    // ---- GRÁFICOS ECHARTS ----
    var option = buildEchartsOption(spec, filas, this.state.anchoContenedor);
    if (!option) return null;

    var self = this;
    return (
      <div className="eric-chart-wrapper" ref={this._wrapperRef}>
        <ReactEcharts
          ref={function(e) { self._echartsComp = e; }}
          option={option}
          style={{ height: '340px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
        {this.renderAviso()}
      </div>
    );
  }
}

export default EricChart;
